import { analytics } from '@repo/utils';
import { ErrorBoundary as SentryErrorBoundary } from '@sentry/react';
import { LocalStorage } from '@vectord/utils';
import { Button, Result } from 'antd';
import React, { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  onError?: (error: Error, info: ErrorInfo) => void;
  fallback?: ReactNode;
  children: ReactNode;
  retry?: Function;
}

interface ErrorBoundaryState {
  error: Error | null;
}

const initialState: ErrorBoundaryState = {
  error: null,
};

const CHUNK_FAILED_KEY = 'chunk_failed';
const ls = LocalStorage.getInstance(CHUNK_FAILED_KEY);

class ReactInternalErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = initialState;
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const { onError } = this.props;
    if (onError) onError(error, info);
  }

  retry = (): void => {
    this.setState(initialState);
    const { retry } = this.props;
    if (retry) {
      retry();
    } else {
      window.location.reload();
    }
  };

  render() {
    const { error } = this.state;
    const { fallback, children } = this.props;

    if (error !== null) {
      if (React.isValidElement(fallback)) {
        return fallback;
      } else {
        return (
          <Result
            status="500"
            title="500"
            subTitle="Something went wrong"
            extra={(
              <Button
                type="primary"
                onClick={this.retry}
              >
                Try Again
              </Button>
            )}
          />
        );
      }
    }
    return children;
  }
}

function ErrorBoundary({ children, ...props }: ErrorBoundaryProps): ReactNode {
  return (
    <SentryErrorBoundary
      beforeCapture={(scope, error: unknown) => {
        const chunkFailedMessage = /Failed to fetch dynamically imported/;
        if ((error as Error)?.message && chunkFailedMessage.test((error as Error).message)) {
          // Set a custom flag to indicate this error was handled
          scope.setTag('handled_in_boundary', 'true');

          analytics.log({
            log_type: 'chunk_failed',
            log_value: (error as Error).message,
          });
          const isExpired = ls?.expiry && Date.now() > ls.expiry;
          if (isExpired || !ls?.expiry) {
            ls.expiry = Date.now() + 10000;
            window.location.reload();
          }
        }
      }}
      fallback={(
        <ReactInternalErrorBoundary
          {...props}
        >
          {children}
        </ReactInternalErrorBoundary>
      )}
    >
      {children}
    </SentryErrorBoundary>
  );
}

export default ErrorBoundary;
